<template>
  <b-row>
    <b-col sm="12">
      <div class="form-container">
        <ShareFormModal
          :entryId="this.entryId"
          :form="this.form"
          ref="shareFormModal"
        />

        <CompleteSupervisionModal
          :entryId="this.entryId"
          :form="this.form"
          :guest="guest"
          ref="CompleteSupervisionModal"
        />

        <b-form v-if="form.pages && form.pages.length > 0" class="form">
          <!-- Floating auto-saving indicator -->
          <div
            v-if="isAutoSaving || showSavedIndicator"
            class="floating-save-indicator"
            :class="{ saved: showSavedIndicator }"
          >
            <b-spinner
              v-if="isAutoSaving"
              small
              variant="dark"
              class="mr-2"
            ></b-spinner>
            <svg
              v-if="showSavedIndicator"
              class="check-icon mr-2"
              viewBox="0 0 24 24"
              width="16"
              height="16"
            >
              <path
                fill="#155724"
                d="M9,20.42L2.79,14.21L5.62,11.38L9,14.77L18.88,4.88L21.71,7.71L9,20.42Z"
              />
            </svg>
            <span class="save-indicator-text">{{
              isAutoSaving ? "Auto-saving..." : "Saved"
            }}</span>
          </div>
          <div
            class="form-page"
            v-for="(page, index) in form.pages"
            :key="index"
          >
            <div v-if="index === form.pages.length - 1" class="tags">
              <b-skeleton-wrapper :loading="!tagsLoaded">
                <template #loading>
                  <b-skeleton width="100%"></b-skeleton>
                </template>
              </b-skeleton-wrapper>

              <div class="tag-entry__tour">
                <Tags
                  v-model="tags"
                  v-if="tagsLoaded && form.identifier !== 'supervision'"
                />
              </div>
            </div>

						<FormPage
                @handleTitleInput="handleTitleInput"
                :page="page"
                :form-data="form"
                :guest="guest"
                :subPage="false"
                :hideFields="hideFields"
                :extraFields="extraFields"
                v-if="pageActivated(page) && !page.repeater && !isActivatedByAnyFormItem(page)"
                :display-in-card="index === form.pages.length -1" />

            <FormPageRepeater
                @handleTitleInput="handleTitleInput"
                :page="page"
                :form-data="form"
                :guest="guest"
                :entryId="entryId"
                :activated-pages="activatedPages"
                v-else-if="pageActivated(page) && page.repeater"/>

          </div>
        </b-form>

        <PreviewEntryModal ref="previewModal" />
        <AutosaveModal
          ref="autosaveModal"
          @leave-without-saving="leaveRoute"
          @save-and-exit="sAndClose"
        />
		<AIOptimizeReviewModal
          ref="aiOptimizeReviewModal"
          @changes-confirmed="handleOptimizationConfirmed"
          @re-optimize-requested="handleReOptimize"
        />
        <LockdownPopup ref="lockdownPopup" />
      </div>
    </b-col>

    <b-col
      sm="12"
      class="mt-3 actions-container footer-buttons__tour"
      v-if="!guest"
    >
      <div
        class="preview-button-container"
        v-b-tooltip.hover
        :title="previewHoverText"
        v-if="form.identifier !== 'supervision'"
      >
        <b-button
          @click="previewEntry"
          class="mt-2 preview-entry"
          variant="link"
          :disabled="!this.isEditing || this.edited"
          >Preview Entry</b-button
        >
      </div>

      <b-button
        :class="{
          'mt-2 mb-0 ovr-button__inverted actions-save': true,
        }"
        @click="share"
        variant="dark"
        v-if="form.identifier === 'supervision'"
        >{{ this.entryId ? "Share with" : "Save and Share with" }}
        {{
          this.form.reviewee ? this.form.reviewee.RevieweeType : "Reviewee"
        }}</b-button
      >
      <AIOptimizeButton
        v-if="aiOptimizationAvailable"
        :is-optimizing="isOptimizingWithAI"
        :disabled="aiOptimizeButtonDisabled"
        :has-content="hasOptimizableContent"
        :has-been-used="hasOptimizedWithAI"
        @optimize="handleAIOptimization"
      />
      <b-button
        class="mt-2 mb-0 ovr-button__inverted actions-save"
        @click="sAndClose"
        variant="dark"
        v-b-tooltip.hover
        title="Save current work and exit"
        >Save and Close</b-button
      >
      <b-button
        :class="{
          'mt-2 mb-0 ovr-button__inverted actions-save': true,
          disabled:
            !this.entryId ||
            !this.form.shared ||
            (this.reviewee && !this.form.reviewee.Signature) ||
            (this.reviewer && this.form.reviewee.Signature),
        }"
        :disabled="
          !this.entryId ||
          (this.reviewee && !this.form.reviewee.Signature) ||
          (this.reviewer && this.form.reviewee.Signature)
        "
        @click="completeSupervision"
        variant="dark"
        v-if="supervision"
        >Sign Off</b-button
      >
      <b-button
        :class="{ 'mt-2 mb-0 ovr-button__inverted actions-save': true }"
        @click="sAndClose"
        variant="dark"
        v-if="
          (supervision &&
            this.entryId &&
            this.reviewee &&
            !this.form.reviewee.Signature) ||
          (this.reviewer && this.form.reviewee.Signature)
        "
        >Close</b-button
      >
    </b-col>

    <b-col
      sm="12"
      class="mt-2 actions-container footer-buttons__tour"
      v-if="!guest && this.entryId"
    >
      <p
        v-if="this.reviewee && !this.form.reviewee.Signature"
        style="width: 100%; text-align: right"
      >
        waiting on Supervisors signature to continue session
      </p>
      <p
        v-if="
          this.reviewer &&
          !this.form.reviewee.RevieweeSignature &&
          this.form.reviewee.Signature
        "
        style="width: 100%; text-align: right"
      >
        waiting on Supervisees signature to complete session
      </p>
    </b-col>

    <b-col
      sm="12"
      class="mt-3 actions-container footer-buttons__tour"
      v-if="guest"
    >
      <b-button
        :class="{
          'mt-2 mb-0 ovr-button__inverted actions-save': true,
          disabled:
            !this.entryId ||
            (this.reviewee && !this.form.reviewee.Signature) ||
            (this.reviewer && this.form.reviewee.Signature),
        }"
        :disabled="
          !this.entryId ||
          (this.reviewee && !this.form.reviewee.Signature) ||
          (this.reviewer && this.form.reviewee.Signature)
        "
        @click="completeSupervision"
        variant="dark"
        >Sign Off</b-button
      >
      <b-button
        :class="{ 'mt-2 mb-0 ovr-button__inverted actions-save': true }"
        @click="sAndClose"
        variant="dark"
        v-if="
          (supervision && this.reviewee && !this.form.reviewee.Signature) ||
          (this.reviewer && this.form.reviewee.Signature)
        "
        >Close</b-button
      >
    </b-col>

    <b-col
      sm="12"
      class="mt-2 actions-container footer-buttons__tour"
      v-if="guest"
    >
      <p
        v-if="this.reviewee && !this.form.reviewee.Signature"
        style="width: 100%; text-align: right"
      >
        waiting on Supervisors signature to continue session
      </p>
      <p
        v-if="
          this.reviewer &&
          !this.form.reviewee.RevieweeSignature &&
          this.form.reviewee.Signature
        "
        style="width: 100%; text-align: right"
      >
        waiting on Supervisees signature to complete session
      </p>
    </b-col>
  </b-row>
</template>

<script>
import FormPage from "./FormPage";
import { EventBus } from "../../event-bus";
import PreviewEntryModal from "../entries/PreviewEntryModal";
import ContentSaveEditIcon from "vue-material-design-icons/ContentSaveEdit";
import ContentSaveIcon from "vue-material-design-icons/ContentSave";
import AutosaveModal from "../entries/AutosaveModal";
import Tags from "@/components/form/Tags";
import { mapGetters } from "vuex";
import { cloneDeep } from "lodash";
import Upload from "./Upload";
import TextField from "./TextField";
import Checkbox from "./Checkbox";
import DropdownFormItem from "@/components/form/DropdownFormItem.vue";
import FormPageRepeater from "@/components/form/FormPageRepeater.vue";
import UserReviewModal from "@/components/organisations/UserReviewModal.vue";
import ShareFormModal from "@/components/form/ShareFormModal.vue";
import CompleteSupervisionModal from "@/components/form/CompleteSupervisionModal.vue";
import { debounce } from "lodash";
import OpenAIService from '../../services/openai';
import AIOptimizeButton from '../common/AIOptimizeButton.vue';
import AIOptimizeReviewModal from '../common/AIOptimizeReviewModal.vue';
import LockdownPopup from '../LockdownPopup.vue';

export default
{
	name: "Form",
	props: {
    form: Object,
    loadForm: Function,
    entryId: [Number, String],
    hideFields: {
      type: Array,
      default: () => [],
    },
    extraFields: {
      type: Array,
      default: () => [],
    },
  },
  components:
  {
    CompleteSupervisionModal,
    ShareFormModal,
    UserReviewModal,
    FormPageRepeater,
    DropdownFormItem,
    Upload,
    Checkbox,
    TextField,
    AutosaveModal,
    ContentSaveIcon,
    ContentSaveEditIcon,
    PreviewEntryModal,
    FormPage,
    Tags,
    LockdownPopup,
    AIOptimizeButton,
    AIOptimizeReviewModal,
  },
  data: function () {
    return {
      title: "",
      busy: false,
      edited: false,
      savedIndicatorTimeout: null,
      bypassSaveCheckTimeout: null,
      continueRoute: null,
      tags: [],
      tagsLoaded: false,
      bypassSaveCheck: false,
      isAutoSaving: false,
      showSavedIndicator: false,
      activatedPages: new Map(), // tracks activated pages
      isOptimizingWithAI: false,
      hasOptimizedWithAI: false, // tracks if AI optimization has been performed
      lastOptimizationData: null, // stores last optimization for reopening modal
    }
  },
  computed:
  {
    getCourseTitle()
    {
      // Return the current courses value using the existing getCourseTitle method
      const coursesPage = this.form.pages.find(item => item.title === "Courses");
      if (coursesPage && coursesPage.items && coursesPage.items.length > 0) {
        return coursesPage.items[0].value;
      }
      return null;
    },
    getCourse()
    {
      // Return the current courses value using the existing getCourse method
      const coursesPage = this.form.pages.find(item => item.title === "Courses");
      if (coursesPage && coursesPage.items && coursesPage.items.length > 0) {
        return coursesPage.items[0];
      }
      return null;
    },
    additionalInformation()
    {
      if (!this.form || !this.form.pages) return [];
      const page = this.form.pages.filter(
        (o) =>
          o.title.includes("Additional Information") && o.items.length === 3
      )[0];
      return page ? page.items : [];
    },
    formData: function () {
      return this.$formTools.flatten(this.form);
    },
    supervision: function () {
      return this.form.identifier === "supervision";
    },
    reviewee: function () {
      if (!this.form.shared) {
        return false;
      }

      // creator and shared with the Supervisor
      if (!this.guest && this.form.reviewee.RevieweeType === "Supervisor") {
        return true;
      }

      // guest and shared with the Supervisee
      else if (this.guest && this.form.reviewee.RevieweeType === "Supervisor") {
        return false;
      }

      if (!this.guest && this.form.reviewee.RevieweeType === "Supervisee") {
        return false;
      }

      // guest and shared with the Supervisee
      else if (this.guest && this.form.reviewee.RevieweeType === "Supervisee") {
        return true;
      }

      return false;
    },
    reviewer: function ()
    {
      if(!this.form.shared)
      {
        return false;
      }

      // creator and shared with the Supervisee
      if (!this.guest && this.form.reviewee.RevieweeType === "Supervisee") {
        return true;
      }

      // guest and shared with the Supervisor
      else if (this.guest && this.form.reviewee.RevieweeType === "Supervisee") {
        return false;
      }

      if (!this.guest && this.form.reviewee.RevieweeType === "Supervisor") {
        return false;
      }

      // guest and shared with the Supervisor
      else if (this.guest && this.form.reviewee.RevieweeType === "Supervisor") {
        return true;
      }

      return false;
    },
    guest: function () {
      return this.form.guest ?? false;
    },
    isEditing: function () {
      return this.entryId != null;
    },
    previewHoverText() {
      if (!this.isEditing || this.edited) {
        return "You must Save and Continue before you can preview the entry.";
      }
      return "";
    },
    ...mapGetters({
      settings: "settings/getSettings",
      isFullMember: 'authentication/isFullMember',
      trialEntriesUsed: 'authentication/getTrialStatus'
    }),
     aiOptimizationAvailable() {
      // Check if organisation has disabled it (org setting takes precedence)
      if(this?.settings?.organisationAiEnabled === false) {
        return false;
      }
      return this?.settings?.aiEnabled;
    },
    hasOptimizableContent() {
      if (!this.form || !this.form.pages) {
        return false;
      }
      
      // Check if there's any text content in pages or subPages
      let hasContent = false;
      
      const checkItems = (items) => {
        if (!items || !Array.isArray(items)) return;
        items.forEach(item => {
          if (item.value && typeof item.value === 'string' && item.value.trim().length > 0) {
            hasContent = true;
          }
        });
      };
      
      this.form.pages.forEach(page => {
        if (hasContent) return;
        checkItems(page.items);
        if (page.subPages) {
          page.subPages.forEach(subPage => checkItems(subPage.items));
        }
      });
      
      return hasContent;
    },
    aiOptimizeButtonDisabled() {
      return !this.aiOptimizationAvailable || 
             !this.hasOptimizableContent || 
             this.isOptimizingWithAI;
    },
    isAtEntryLimit() {
      if (this.isFullMember || !this.trialEntriesUsed) {
        return false;
      }
      return this.trialEntriesUsed.trialEntriesUsed >= 5;
    },
  },
  mounted: function ()
  {

  },
  methods:
  {
    resetCourseStandards()
    {
      const allActivators = this.getCourse.activators;

      for (const activatorKey in allActivators)
      {
        const standards = allActivators[activatorKey];
        for (const standard of standards)
        {
          let items = this.$formTools.findItemsByIdentifier(this.form, standard.identifier);
          if (items.length > 0)
          {
            items.forEach(item =>
            {
              item.value = "0";
            });
          }
        }
      }
    },
    completeSupervision()
    {
      this.$refs.CompleteSupervisionModal.show();
    },
    handleTitleInput(event)
    {
      this.$emit('handleTitleInput');
    },
    formattedDate(months)
    {
      let today = new Date();
      today.setMonth(today.getMonth() + months);
      let yyyy = today.getFullYear();
      let mm = today.getMonth() + 1; // Months start at 0!
      let dd = today.getDate();

      if (dd < 10) dd = "0" + dd;
      if (mm < 10) mm = "0" + mm;

      return yyyy + "-" + mm + "-" + dd;
    },
		formChanged()
    {
			this.edited = true;
		},
    preSelectFieldsFor(activated_page_item,identifier,select,hide,reminder)
    {
      if(select)
      {
        this.setSelectedFor(activated_page_item,identifier,select);
      }

      if(hide)
      {
        this.setHiddenFor(activated_page_item,identifier,hide);
      }

      if(reminder)
      {
        this.setReminderFor(activated_page_item,identifier,reminder);
      }
    },
    setSelectedFor(activated_page_item,identifier,select)
    {
      if (this.getTitle().includes(identifier) &&
        select.some(element => activated_page_item.title.includes(' '+element+' ')))
      {

        activated_page_item.value = "1";
      }
    },
    setHiddenFor(activated_page_item,identifier,hide)
    {
      if (this.getTitle().includes(identifier) &&
          hide.some(element => activated_page_item.title.includes(' '+element+' ')))
      {
        activated_page_item.visible = false;
      }
    },
    setReminderFor(activated_page_item,identifier,months)
    {
      if(this.getTitle().includes(identifier) && activated_page_item.identifier === "DiaryReminderDate")
      {
        activated_page_item.value = this.formattedDate(months);
      }
    },
    getTitle()
    {
      return this.form.pages[1].items[0].value;
    },
    validateCurrentForm()
    {
      let [validated, failed_field] = this.$formTools.validate(this.form);

      if (!validated)
      {
        this.$emit("show-message",
        {
          message: "The " + failed_field + " field is not provided",
          type: "danger",
        });

        document.getElementById("main-header").scrollIntoView(
        {
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      }

      return validated;
    },
    share() {
      if (!this.validateCurrentForm()) {
        return;
      }

      const showShareModal = () => {
        this.$refs.shareFormModal.show();
      };

      if (this.entryId) {
        this.saveEntry(showShareModal);
        return;
      }

      this.submitEntry(showShareModal);
    },
		sAndClose()
    {
      if(this.guest)
      {
        this.bypassSaveCheck = true;
        this.$router.push({ name: "entries" });
      } else if (this.isEditing) {
        this.saveAndClose();
      } else {
        this.submitAndClose();
      }
    },
    sAndContinue(suppressSuccessMessage = false)
    {
      if (this.isEditing)
      {
        this.saveAndContinue(suppressSuccessMessage);
      }
      else
      {
        this.submitAndContinue(suppressSuccessMessage);
      }
    },
		pageActivated: function (page)
    {
			if (!page.requiresActivation) return true;

			let data = this.formData;
			for (let key in page.activatedBy)
      {
				if (page.activatedBy.hasOwnProperty(key))
        {
          // Check if the key directly exists in the Map
          if (data.has(key))
          {
            let formValue = data.get(key);

            if (page.activatedBy[key].includes(formValue))
            {
              return true;
            }
          }

          // If we didn't find a direct match, check entries that might be arrays or nested Maps
          for (const [mapKey, mapValue] of data.entries())
          {
            // Handle case where value is an array
            if (Array.isArray(mapValue))
            {
              // Check each item in the array
              for (const arrayItem of mapValue)
              {
                // If item is a Map or has Map-like interface
                if (arrayItem && typeof arrayItem === 'object' && arrayItem.has && arrayItem.get && arrayItem.has(key))
                {
                  if (page.activatedBy[key].includes(arrayItem.get(key)))
                    {
                      // Store array item activation
                      this.activatedPages.set(page.id, {
                        activatorKey: key,
                        activatorValue: arrayItem.get(key),
                        arrayKey: mapKey,
                        itemIdentifier: key
                      });
                      return true;
                    }
                }

              }
            }
          }
				}
			}

      [page, ...(page.subPages || [])].forEach(hiddenPage => {
        (hiddenPage.items || []).forEach(this.$formTools.clearItemAndDescendants);
      });

			return false;
		},
    submitAndContinue(suppressSuccessMessage = false) {
      //Cancels autosave on manual save
      if (!suppressSuccessMessage) {
        this.cancelAutosave();
      }
      this.submitEntry(() => {
        let routeName = "edit/" + this.form.identifier;
        //Stops modal showing when route is changed
        this.bypassSaveCheck = true;
        this.$router._isShallowNavigation = true;
        this.$router.push({ name: routeName, params: { id: this.entryId } });
        if (!suppressSuccessMessage) {
          this.$success(
            "Entry saved successfully",
            "You can continue make changes to it."
          );
        }
      });
    },
    submitAndClose() {
      this.submitEntry(() => {
        EventBus.$emit("entry-submitted");
        EventBus.$emit("show-toast", {
          title: "Entry Submitted",
          message: "You can edit it under the 'Entries' section",
        });
        this.$router.push({ name: "entries" });
      });
    },
    submitEntry: function (successCallback)
    {
        if (!this.isFullMember && this.trialEntriesUsed.trialEntriesUsed >= 5) {
            this.$refs.lockdownPopup.show();
            return;
        }
      if (!this.validateCurrentForm()) {
        return;
      }

      let data = this.buildSubmissionData();

      if (this.form.identifier === "supervision")
      {
        data.set("Field11", new Date().toISOString().slice(0, 10));
      }

      return this.$api.v2
        .post(
          "entries/" + this.form.identifier,
          this.$formTools.mapToJson(data)
        )
        .then((resp) =>
        {
          this.entryId = resp.body.entryId;

          return this.$api.v2
            .put(`entries/${this.entryId}/tags`, { tags: this.tags })
            .then(() => {
              successCallback();
            });
        })
        .catch((errResp) => {
          this.$httpError('Failed to create entry', errResp);
          // Only reload form on server errors (5xx), not validation errors (4xx)
          if (errResp.status >= 500) {
            this.loadForm();
          }
        })
        .finally(() => {
          this.handleSaveCompletion();
        });
    },
    saveAndClose() {
      this.saveEntry(() => {
        EventBus.$emit("show-toast", {
          title: "Entry saved successfully",
          message: "You can edit it under the 'Entries' section.",
        });
        this.$router.push({ name: "entries" });
      });
    },
    saveAndContinue(suppressSuccessMessage = false) {
      this.saveEntry(() => {
        // Only show success message if not suppressed
        if (!suppressSuccessMessage) {
          this.$success(
            "Entry saved successfully",
            "You can continue make changes to it."
          );
        }
      });
    },
    saveEntry: function (successCallback) {
      if (!this.validateCurrentForm()) {
        return;
      }

      let data = this.buildSubmissionData();

      if (this.form.identifier === "supervision") {
        data.set("Field11", new Date().toISOString().slice(0, 10));
      }

      // When updating an entry, always include the date field to preserve the original date
      if (this.entryId) {
        const dateFieldByType = {
          'diary': 'Field11',
          'reflection': 'Field14',
          'supervision': 'Field1'
        };
        const dateField = dateFieldByType[this.form.identifier];
        if (dateField && !data.has(dateField)) {
          // Find the date field in the form structure and include its value
          const dateItems = this.$formTools.findItemsByIdentifier(this.form, dateField);
          if (dateItems.length > 0 && dateItems[0].value) {
            data.set(dateField, dateItems[0].value);
          }
        }
      }

      return this.$api.v2
        .post(
          "entries/" + this.entryId + "/update",
          this.$formTools.mapToJson(data)
        )
        .then(() => {
          return this.$api.v2.put(`entries/${this.entryId}/tags`, {
            tags: this.tags,
          });
        })
        .then(() => {
          successCallback();
        })
        .catch((errResp) => {
          this.$httpError('Failed to save entry', errResp);
          // Only reload form on server errors (5xx), not validation errors (4xx)
          if (errResp.status >= 500) {
            this.loadForm();
          }
        })
        .finally(() => {
          this.handleSaveCompletion();
        });
    },
    buildSubmissionData() {
      let data = this.$formTools.flatten(this.form);

      // Supervision party fields can sit on inactive activator branches while
      // hidden via hideFields, so flatten() may omit them even though
      // setItemValue() updated them. Always include all four party fields.
      if (this.form.identifier === "supervision") {
        for (const field of [
          "supervisor",
          "supervisor_role",
          "supervise",
          "supervise_role",
        ]) {
          const items = this.$formTools.findItemsByIdentifier(this.form, field);
          if (items.length > 0) {
            data.set(field, items[0].value ?? "");
          }
        }
      }

      if (this.form.extra !== undefined) {
        data.set("extra", this.form.extra);
      }
      return data;
    },
    previewEntry() {
      this.$refs.previewModal.show(this.entryId + "");
    },
    handleSaveCompletion() {
      this.edited = false;
      this.bypassSaveCheck = true;

      if (this.bypassSaveCheckTimeout) {
        clearTimeout(this.bypassSaveCheckTimeout);
      }
      this.bypassSaveCheckTimeout = setTimeout(() => {
        this.bypassSaveCheck = false;
      }, 3000);

      // Show saved indicator for autosave
      if (this.isAutoSaving) {
        this.showSavedIndicator = true;
        this.isAutoSaving = false;
        if (this.savedIndicatorTimeout) {
          clearTimeout(this.savedIndicatorTimeout);
        }
        this.savedIndicatorTimeout = setTimeout(() => {
          this.showSavedIndicator = false;
        }, 2000);
      }
    },
    leavingRoute(to, from, next) {
      if (this.edited && !this.bypassSaveCheck) {
        this.$refs.autosaveModal.show();
        this.continueRoute = next;
      } else {
        next();
      }
    },
    leaveRoute() {
      this.bypassSaveCheck = true;
      if (this.continueRoute) {
        this.continueRoute();
      }
    },
    loadEntryTags() {
      if (!this.entryId || this.guest) {
        this.tagsLoaded = true;
        return;
      }
      this.tagsLoaded = false;
      this.$api.v2
        .get(`entries/${this.entryId}/tags`)
        .then((resp) => {
          // resp.body is the tags array directly, not {tags: [...]}
          this.tags = Array.isArray(resp.body) ? resp.body : (resp.body.tags || []);
          this.tagsLoaded = true;
        })
        .catch((err) => {
          this.$httpError("failed to load entry tags", err);
          this.tags = [];
          this.tagsLoaded = true;
        });
    },
    onFormSavingStarted() {
      if (this.isEditing || !this.isAtEntryLimit) {
        this.isAutoSaving = true;
        this.edited = true;
        this.formSave();
      }
    },
    cancelAutosave() {
      if (this.formSave) {
        this.formSave.cancel();
        this.isAutoSaving = false;
      }
    },
    // Check if a page is activated by any form item (and therefore shown elsewhere)
    isActivatedByAnyFormItem: function(page)
    {
      if (!page.requiresActivation) return false;

      for (let key in page.activatedBy)
      {
        // Also check if this page is in the activatedPages map
        if (this.activatedPages && this.activatedPages.has(page.id))
        {
          return true;
        }
      }

      return false;
    },
    /**
     * Handle AI optimization request
     */
    async handleAIOptimization() {
      // If there's existing optimization data, just reopen the modal
      if (this.lastOptimizationData && this.$refs.aiOptimizeReviewModal.hasExistingChanges()) {
        this.$refs.aiOptimizeReviewModal.show(
          this.lastOptimizationData.optimizedContent,
          this.lastOptimizationData.visiblePages
        );
        return;
      }
      
      if (!this.isFullMember) {
        this.$refs.lockdownPopup.show();
        return;
      }
      
      if (this.isOptimizingWithAI) {
        return;
      }
      
      // Auto-save before optimizing if there are unsaved changes (don't wait)
      if (this.edited) {
        this.sAndContinue(true);
      }

      this.isOptimizingWithAI = true;
      
      // Show modal immediately with loading state
      this.$refs.aiOptimizeReviewModal.showLoading();
      
      try {
        // Filter to only visible pages
        const visiblePages = this.form.pages.filter(page => this.pageActivated(page));
        const visibleForm = {
          ...this.form,
          pages: visiblePages
        };
        
        let optimizedContent;
        switch (visibleForm.identifier) {
          case 'diary':
            optimizedContent = await OpenAIService.optimizeDiaryEntry(visibleForm);
            break;
          case 'supervision':
            optimizedContent = await OpenAIService.optimizeSupervisionEntry(visibleForm);
            break;
          case 'reflection':
            optimizedContent = await OpenAIService.optimizeReflectionEntry(visibleForm);
            break;
          default:
            optimizedContent = await OpenAIService.optimizeDiaryEntry(visibleForm);
        }
        
        // Store optimization data for reopening
        this.lastOptimizationData = {
          optimizedContent,
          visiblePages
        };
        
        // Mark that AI optimization has been performed
        this.hasOptimizedWithAI = true;
        
        // Clear any previous changes and show results in already-open modal
        this.$refs.aiOptimizeReviewModal.clearChanges();
        this.$refs.aiOptimizeReviewModal.showResults(optimizedContent, visiblePages);
        
      } catch (error) {
        // Hide modal on error
        this.$refs.aiOptimizeReviewModal.hideLoading();
        
        EventBus.$emit('show-toast', {
          title: 'AI Optimization Failed',
          message: error.message || 'An error occurred while optimizing the form',
          variant: 'danger'
        });
      } finally {
        this.isOptimizingWithAI = false;
      }
    },
    /**
     * Handle request to re-optimize from the modal
     */
    async handleReOptimize() {
      this.$refs.aiOptimizeReviewModal.closeModal();
      
      // Clear stored optimization data to force fresh optimization
      this.lastOptimizationData = null;
      this.$refs.aiOptimizeReviewModal.clearChanges();
      
      // Run optimization again
      await this.handleAIOptimization();
    },
    
    /**
     * Handle confirmation of optimized changes from the review modal
     * @param {number} appliedCount - Number of changes applied
     */
    handleOptimizationConfirmed(appliedCount) {
      // Mark form as edited and save if changes were applied
      if (appliedCount > 0) {
        this.edited = true;
        // Auto-save the applied changes
        this.sAndContinue(true);
      }
      
      // Show success message
      EventBus.$emit('show-toast', {
        title: 'AI Optimization Applied',
        message: appliedCount > 0 
          ? `Successfully applied ${appliedCount} change${appliedCount !== 1 ? 's' : ''}. Entry saved.` 
          : 'No changes were applied',
      });
    },
  },
  watch:
  {
    getCourseTitle:
    {
      handler: function(newValue, oldValue)
      {
        // This will trigger whenever the courses dropdown changes
        if (newValue !== oldValue)
        {

          // First, reset all checkboxes from all activators to "0"
          this.resetCourseStandards();

          // Then set the selected standards to "1"
          let standards = this.getCourse.activators[newValue];

          if(!standards)
          {
            return;
          }

          for (const standard of standards)
          {
            let items = this.$formTools.findItemsByIdentifier(this.form, standard.identifier);
            if (items && items.length > 0)
            {
              // Update all matching items
              items.forEach(item =>
              {
                item.value = "1";
              });
            }
          }
        }
      }
    },
    busy: function (newValue) {
      // Loading events removed - handled by shallow routing
    },
  },
  onIdle() {
    this.sAndContinue();
  },
  created() {
    // Delay loadEntryTags to next tick to ensure entryId prop is set
    this.$nextTick(() => {
      this.loadEntryTags();
    });
    // Listen for auto-saving events from form components
    EventBus.$on("form-saving-started", this.onFormSavingStarted);
    this.formSave = debounce(() => {
      this.sAndContinue(true);
    }, 10000);
  },
  beforeDestroy() {
    EventBus.$off("form-saving-started", this.onFormSavingStarted);
    this.cancelAutosave();
    if (this.savedIndicatorTimeout) {
      clearTimeout(this.savedIndicatorTimeout);
    }
    if (this.bypassSaveCheckTimeout) {
      clearTimeout(this.bypassSaveCheckTimeout);
    }
  },
};
</script>

<style>
.form-container label,
.form-container legend {
  font-weight: bold;
}

/* Global saving/saved states for form inputs */
.saving {
  background-color: #ffebee !important;
  border-color: #f44336 !important;
  color: #d32f2f;
}

.saving:focus {
  box-shadow: 0 0 0 0.2rem rgba(244, 67, 54, 0.25) !important;
}

.saved {
  background-color: #e8f5e8 !important;
  border-color: #4caf50 !important;
  color: #2e7d32;
}

.saved:focus {
  box-shadow: 0 0 0 0.2rem rgba(76, 175, 80, 0.25) !important;
}
</style>

<style scoped lang="scss">
.form {
  width: 100%;
  margin-right: auto;
}

.form-container {
  width: 100%;
}

.diary-form__right {
  margin-top: 30px;
}

.actions-container {
  display: flex;
  justify-content: right;
}

.actions-add-topic .actions-save {
  margin-left: 0 !important;
}

.actions-save {
  background: $blue-300 !important;
  font-weight: 600;
  border-radius: 8px;
  padding: 0.375rem 22px;
  margin-left: 30px;
}

.preview-entry {
  color: $blue-300;
}

.preview-button-container {
  display: inline-block;
}

.auto-saving-indicator {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background-color: #ffebee;
  border: 1px solid #f44336;
  border-radius: 4px;
  color: #d32f2f;
}

.auto-saving-text {
  font-size: 14px;
  font-weight: 500;
}

.floating-save-indicator {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1050;
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: #ffc107;
  border: 1px solid #ffc107;
  border-radius: 0.375rem;
  color: #212529;
  font-size: 0.875rem;
  font-weight: 400;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  transition: all 0.15s ease-in-out;
  min-width: 140px;
}

.floating-save-indicator.saved {
  background-color: #d4edda;
  border-color: #c3e6cb;
  color: #155724;
}

.floating-save-indicator .check-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.save-indicator-text {
  margin: 0;
  white-space: nowrap;
}

@media screen and (max-width: 950px) {
  .actions-container {
    text-align: center;
  }

  .actions-container > * {
    display: block;
    margin: 0 auto;
  }
}
</style>
